// Workspace profiles — each user picks the use case that matches their work.
// This drives dashboard layout, accent colour, terminology, default target
// type, tab order and the investigation playbooks offered in the command bar.
// Dependency-free so it is safe on both client and server.

export type WorkspaceId =
  | "investigator"
  | "lawyer"
  | "individual"
  | "startup"
  | "vc"
  | "enterprise"
  | "journalist"
  | "compliance"
  | "other";

export type DashboardTab =
  | "profile"
  | "overview"
  | "business"
  | "graph"
  | "timeline"
  | "findings"
  | "infra"
  | "report";

export type Playbook = {
  id: string;
  label: string;
  hint: string;
  /** How to shape the query sent to the collectors. */
  mode: "scan" | "business";
  targetTypeOverride?: "person" | "organization";
  /** Extra instruction appended to the refine "notes" field. */
  notes?: string;
};

export type WorkspaceProfile = {
  id: WorkspaceId;
  label: string;
  blurb: string;
  /** lucide icon name rendered by the picker */
  icon: string;
  /** oklch accent used for gauges, rails and highlights */
  accent: string;
  eyebrow: string;
  headline: string;
  searchPlaceholder: string;
  scoreLabel: string;
  defaultTargetType: "auto" | "person" | "organization";
  tabs: DashboardTab[];
  /** Refine fields surfaced first for this workspace. */
  priorityRefine: string[];
  kpis: Array<{ key: "trust" | "confidence" | "sources" | "findings" | "entities"; label: string }>;
  playbooks: Playbook[];
  aiPrompts: string[];
};

const CYAN = "oklch(0.78 0.16 195)";

export const WORKSPACES: Record<WorkspaceId, WorkspaceProfile> = {
  investigator: {
    id: "investigator",
    label: "Private investigator",
    blurb: "Subject-first casework, custody trail and pivoting.",
    icon: "Fingerprint",
    accent: CYAN,
    eyebrow: "Field investigation console",
    headline: "Run a subject investigation",
    searchPlaceholder: "Subject name · email · phone · username · address…",
    scoreLabel: "Trust",
    defaultTargetType: "person",
    tabs: ["profile", "overview", "graph", "timeline", "findings", "infra", "report"],
    priorityRefine: ["dob", "location", "address", "phone", "email", "employer", "aliases", "notes"],
    kpis: [
      { key: "trust", label: "Trust" },
      { key: "confidence", label: "Confidence" },
      { key: "findings", label: "Findings" },
      { key: "entities", label: "Entities" },
    ],
    playbooks: [
      { id: "identity", label: "Identity resolution", hint: "Confirm the right person and rule out look-alikes", mode: "scan", targetTypeOverride: "person", notes: "Prioritise identity disambiguation and address history." },
      { id: "assets", label: "Asset & affiliation trace", hint: "Companies, roles and holdings", mode: "business" },
      { id: "records", label: "Court & public records", hint: "Litigation, filings and enforcement", mode: "scan", targetTypeOverride: "person", notes: "Prioritise court records, filings and enforcement actions." },
    ],
    aiPrompts: ["Who is this person, exactly?", "List look-alike risks", "Any litigation history?", "What should I pivot on next?"],
  },
  lawyer: {
    id: "lawyer",
    label: "Law firm / counsel",
    blurb: "Litigation posture, filings, and citable primary sources.",
    icon: "Scale",
    accent: "oklch(0.72 0.13 255)",
    eyebrow: "Legal research console",
    headline: "Build an evidence-backed profile",
    searchPlaceholder: "Party name · company · counsel · docket reference…",
    scoreLabel: "Trust",
    defaultTargetType: "auto",
    tabs: ["profile", "findings", "business", "timeline", "graph", "overview", "report"],
    priorityRefine: ["employer", "location", "aliases", "dob", "address", "email", "phone", "notes"],
    kpis: [
      { key: "trust", label: "Trust" },
      { key: "confidence", label: "Confidence" },
      { key: "sources", label: "Sources" },
      { key: "findings", label: "Findings" },
    ],
    playbooks: [
      { id: "litigation", label: "Litigation history", hint: "Dockets, judgments and regulatory actions", mode: "scan", notes: "Focus on court records, dockets, judgments and regulator actions. Cite primary documents." },
      { id: "counterparty", label: "Counterparty due diligence", hint: "Ownership, officers and exposure", mode: "business" },
      { id: "sanctions", label: "Sanctions & adverse media", hint: "Watchlists and negative press", mode: "scan", notes: "Focus on sanctions lists, PEP status and adverse media." },
    ],
    aiPrompts: ["Summarise the filings", "Cite the primary documents", "Any adverse media?", "Draft a due-diligence memo"],
  },
  individual: {
    id: "individual",
    label: "Individual / personal",
    blurb: "Check your own exposure, breaches and public footprint.",
    icon: "UserRound",
    accent: "oklch(0.75 0.15 150)",
    eyebrow: "Personal exposure console",
    headline: "Check what the internet knows about you",
    searchPlaceholder: "Your email · phone · username · full name…",
    scoreLabel: "Safety",
    defaultTargetType: "person",
    tabs: ["profile", "findings", "timeline", "overview", "report"],
    priorityRefine: ["email", "phone", "location", "aliases", "dob", "employer", "address", "notes"],
    kpis: [
      { key: "trust", label: "Safety" },
      { key: "confidence", label: "Confidence" },
      { key: "findings", label: "Exposures" },
    ],
    playbooks: [
      { id: "breach", label: "Breach exposure", hint: "Where your credentials have leaked", mode: "scan", targetTypeOverride: "person", notes: "Focus on credential breaches and leaked accounts." },
      { id: "footprint", label: "Public footprint", hint: "Profiles, mentions and images", mode: "scan", targetTypeOverride: "person", notes: "Focus on public social profiles and mentions." },
      { id: "impersonation", label: "Impersonation check", hint: "Look-alike accounts and domains", mode: "scan", notes: "Focus on impersonating accounts, look-alike domains and fake profiles." },
    ],
    aiPrompts: ["What should I remove first?", "Am I in any breaches?", "How exposed is my phone number?", "How do I reduce this score?"],
  },
  startup: {
    id: "startup",
    label: "Startup / founder",
    blurb: "Vet investors, partners and hires; watch competitors.",
    icon: "Rocket",
    accent: "oklch(0.78 0.17 90)",
    eyebrow: "Founder intelligence console",
    headline: "Vet a partner, investor or competitor",
    searchPlaceholder: "Company · investor · founder · domain…",
    scoreLabel: "Trust",
    defaultTargetType: "organization",
    tabs: ["business", "profile", "overview", "graph", "findings", "timeline", "report"],
    priorityRefine: ["employer", "location", "email", "aliases", "notes", "dob", "address", "phone"],
    kpis: [
      { key: "trust", label: "Trust" },
      { key: "confidence", label: "Confidence" },
      { key: "entities", label: "Entities" },
      { key: "sources", label: "Sources" },
    ],
    playbooks: [
      { id: "competitors", label: "Competitor landscape", hint: "Rivals, their leadership and positioning", mode: "business" },
      { id: "investor", label: "Investor background", hint: "Track record and portfolio", mode: "business" },
      { id: "hire", label: "Key-hire background", hint: "Verify a senior candidate", mode: "scan", targetTypeOverride: "person", notes: "Verify employment history, credentials and public reputation." },
    ],
    aiPrompts: ["Who are the direct competitors?", "Who runs this company?", "Any red flags for a partnership?", "How are they funded?"],
  },
  vc: {
    id: "vc",
    label: "VC / investor",
    blurb: "Deal diligence: cap table signals, founders, competitors.",
    icon: "TrendingUp",
    accent: "oklch(0.74 0.16 300)",
    eyebrow: "Deal diligence console",
    headline: "Diligence a company and its people",
    searchPlaceholder: "Company · founder · fund · domain…",
    scoreLabel: "Trust",
    defaultTargetType: "organization",
    tabs: ["business", "overview", "profile", "graph", "findings", "timeline", "report"],
    priorityRefine: ["employer", "location", "email", "aliases", "notes", "dob", "address", "phone"],
    kpis: [
      { key: "trust", label: "Trust" },
      { key: "confidence", label: "Confidence" },
      { key: "entities", label: "People" },
      { key: "findings", label: "Flags" },
    ],
    playbooks: [
      { id: "dd", label: "Company diligence", hint: "Officers, ownership, competitors", mode: "business" },
      { id: "founder", label: "Founder check", hint: "History, prior ventures, reputation", mode: "scan", targetTypeOverride: "person", notes: "Focus on prior ventures, exits, failures and reputation." },
      { id: "market", label: "Competitive set", hint: "Who else is in this market", mode: "business" },
    ],
    aiPrompts: ["Who are the key people?", "What are the competitors?", "Any governance red flags?", "Summarise the funding story"],
  },
  enterprise: {
    id: "enterprise",
    label: "Enterprise / security",
    blurb: "Third-party risk, infrastructure exposure and threat posture.",
    icon: "Building2",
    accent: "oklch(0.70 0.14 25)",
    eyebrow: "Enterprise risk console",
    headline: "Assess a third party or threat",
    searchPlaceholder: "Domain · IP · vendor · organisation…",
    scoreLabel: "Trust",
    defaultTargetType: "organization",
    tabs: ["overview", "infra", "findings", "business", "graph", "timeline", "profile", "report"],
    priorityRefine: ["employer", "location", "email", "notes", "aliases", "address", "phone", "dob"],
    kpis: [
      { key: "trust", label: "Trust" },
      { key: "confidence", label: "Confidence" },
      { key: "sources", label: "Collectors" },
      { key: "findings", label: "Findings" },
    ],
    playbooks: [
      { id: "vendor", label: "Vendor risk", hint: "Ownership, breaches, exposure", mode: "business" },
      { id: "attack", label: "Attack surface", hint: "Domains, hosts, certificates", mode: "scan", notes: "Focus on infrastructure, DNS, certificates and exposed services." },
      { id: "threat", label: "Threat actor profile", hint: "Infrastructure and reputation", mode: "scan", notes: "Focus on threat reputation, malicious infrastructure and abuse reports." },
    ],
    aiPrompts: ["What is the attack surface?", "Any breach exposure?", "Who owns this infrastructure?", "Summarise vendor risk"],
  },
  journalist: {
    id: "journalist",
    label: "Journalist / researcher",
    blurb: "Source verification, document trails and connections.",
    icon: "Newspaper",
    accent: "oklch(0.80 0.12 70)",
    eyebrow: "Newsroom research console",
    headline: "Verify a source or storyline",
    searchPlaceholder: "Person · company · document · handle…",
    scoreLabel: "Trust",
    defaultTargetType: "auto",
    tabs: ["profile", "findings", "timeline", "graph", "business", "overview", "report"],
    priorityRefine: ["employer", "location", "aliases", "notes", "dob", "email", "phone", "address"],
    kpis: [
      { key: "trust", label: "Trust" },
      { key: "confidence", label: "Confidence" },
      { key: "sources", label: "Sources" },
      { key: "entities", label: "Connections" },
    ],
    playbooks: [
      { id: "verify", label: "Verify a claim", hint: "Cross-check against primary sources", mode: "scan", notes: "Cross-check every claim against primary sources and note conflicts." },
      { id: "network", label: "Connection map", hint: "Who is linked to whom", mode: "business" },
      { id: "docs", label: "Document trail", hint: "Filings, PDFs and archives", mode: "scan", notes: "Prioritise primary documents: filings, PDFs, archives." },
    ],
    aiPrompts: ["What is verified vs unverified?", "Cite the primary documents", "Who are the connected people?", "Where do sources conflict?"],
  },
  compliance: {
    id: "compliance",
    label: "Compliance / KYC",
    blurb: "Sanctions, PEP, UBO and adverse-media screening.",
    icon: "ShieldCheck",
    accent: "oklch(0.76 0.14 175)",
    eyebrow: "Screening console",
    headline: "Screen a customer or counterparty",
    searchPlaceholder: "Individual · company · registration number…",
    scoreLabel: "Trust",
    defaultTargetType: "auto",
    tabs: ["findings", "business", "profile", "overview", "timeline", "graph", "report"],
    priorityRefine: ["dob", "location", "employer", "address", "aliases", "email", "phone", "notes"],
    kpis: [
      { key: "trust", label: "Trust" },
      { key: "confidence", label: "Confidence" },
      { key: "findings", label: "Hits" },
      { key: "sources", label: "Lists checked" },
    ],
    playbooks: [
      { id: "kyc", label: "KYC screening", hint: "Sanctions, PEP and watchlists", mode: "scan", notes: "Focus on sanctions, PEP status, watchlists and adverse media." },
      { id: "ubo", label: "UBO / beneficiary check", hint: "Owners, officers and control", mode: "business" },
      { id: "adverse", label: "Adverse media", hint: "Negative press and enforcement", mode: "scan", notes: "Focus on adverse media, enforcement actions and regulatory penalties." },
    ],
    aiPrompts: ["Any sanctions or PEP hits?", "Who are the beneficial owners?", "Summarise adverse media", "What is unresolved?"],
  },
  other: {
    id: "other",
    label: "General intelligence",
    blurb: "The full CARIO surface with every module enabled.",
    icon: "Radar",
    accent: CYAN,
    eyebrow: "Command",
    headline: "Run an investigation",
    searchPlaceholder: "Enter email · domain · IP · wallet · username · phone · org…",
    scoreLabel: "Trust",
    defaultTargetType: "auto",
    tabs: ["profile", "overview", "business", "graph", "timeline", "findings", "infra", "report"],
    priorityRefine: ["dob", "location", "address", "employer", "email", "phone", "aliases", "notes"],
    kpis: [
      { key: "trust", label: "Trust" },
      { key: "confidence", label: "Confidence" },
    ],
    playbooks: [
      { id: "standard", label: "Standard sweep", hint: "All collectors, no bias", mode: "scan" },
      { id: "company", label: "Company intel", hint: "Officers and competitors", mode: "business" },
    ],
    aiPrompts: ["Summarise this scan", "Most suspicious finding?", "What should I pivot on?", "Broaden the search"],
  },
};

export const WORKSPACE_IDS = Object.keys(WORKSPACES) as WorkspaceId[];

/** Legacy audience values stored before workspaces existed. */
export function normalizeWorkspace(value: string | null | undefined): WorkspaceId {
  if (!value) return "other";
  if (value === "investor") return "vc";
  return (WORKSPACE_IDS as string[]).includes(value) ? (value as WorkspaceId) : "other";
}

export function getWorkspace(id: string | null | undefined): WorkspaceProfile {
  return WORKSPACES[normalizeWorkspace(id)];
}

export const TAB_LABELS: Record<DashboardTab, string> = {
  profile: "Profile",
  overview: "Overview",
  business: "Business intel",
  graph: "Network",
  timeline: "Timeline",
  findings: "Findings",
  infra: "Infrastructure",
  report: "Dossier",
};